import { useCallback, useEffect, useRef, useState } from 'react';

export type PriceMap = Record<string, number>;

let _lastRefresh: (() => Promise<void>) | null = null;

export function bindRefresh(fn: () => Promise<void>) {
  _lastRefresh = fn;
}

export async function refreshPrices() {
  if (_lastRefresh) return _lastRefresh();
  return undefined;
}

export { refreshPrices as refresh };

export function usePrices(opts?: { intervalMs?: number }) {
  const intervalMs = opts?.intervalMs ?? 10000;
  const [prices, setPrices] = useState<PriceMap>({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const inFlight = useRef<AbortController | null>(null);
  const hasData = useRef(false);

  const fetchPrices = useCallback(async (isPoll = false) => {
    inFlight.current?.abort();
    const ac = new AbortController();
    inFlight.current = ac;
    const hadData = hasData.current;
    try {
      if (!hadData) setInitialLoading(true);
      else if (isPoll) setRefreshing(true);
      setError(null);

      let raw: Record<string, unknown> | null = null;
      const endpoints = ['/api/prices', '/api/price'];
      for (const url of endpoints) {
        try {
          const r = await fetch(url, {
            method: 'GET',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal: ac.signal,
          });
          if (!r.ok) continue;
          raw = await r.json();
          break;
        } catch {
          /* try next endpoint */
        }
      }
      if (!raw) {
        try {
          const walletRes = await fetch('/api/wallet/summary', {
            method: 'GET',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal: ac.signal,
          });
          if (walletRes.ok) {
            const walletJson = await walletRes.json();
            const walletPrices: Record<string, number> = {};
            if (Array.isArray(walletJson?.coins)) {
              for (const entry of walletJson.coins) {
                const sym = typeof entry?.coin === 'string' ? entry.coin.toUpperCase() : null;
                const price = typeof entry?.price === 'number' ? entry.price : null;
                if (sym && price != null) walletPrices[sym] = price;
              }
            }
            if (Object.keys(walletPrices).length > 0) {
              raw = walletPrices;
            }
          }
        } catch {
          /* fallback suppressed */
        }
      }
      if (raw && raw.ok === false) {
        setError(typeof raw.error === 'string' ? raw.error : 'Price feed unavailable');
        raw = null;
      }

      if (raw) {
        const upper: PriceMap = {} as PriceMap;
        for (const [k, v] of Object.entries(raw)) {
          const key = k.toLowerCase();
          if (["ts", "timestamp", "vs", "ok", "message"].includes(key)) continue;
          const num = Number(v);
          if (Number.isFinite(num)) upper[k.toUpperCase()] = num;
        }
        setPrices(upper);
        setLastUpdated(Date.now());
        if (!hadData) setInitialLoading(false);
        hasData.current = true;
        setError(null);
      } else if (!hadData) {
        // eslint-disable-next-line no-console
        console.warn('[prices] failed to load price data');
        setError('Price feed unavailable — see System Status');
      }
    } catch {
      setError('Price feed unavailable — see System Status');
    } finally {
      if (!hadData) setInitialLoading(false);
      if (isPoll) setRefreshing(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchPrices();
  }, [fetchPrices]);

  bindRefresh(refresh);

  useEffect(() => {
    fetchPrices();
    if (intervalMs > 0) {
      timer.current = window.setInterval(() => fetchPrices(true), intervalMs) as unknown as number;
    }
    return () => {
      if (timer.current) window.clearInterval(timer.current);
      inFlight.current?.abort();
    };
  }, [fetchPrices, intervalMs]);

  return { prices, lastUpdated, initialLoading, refreshing, refresh, error } as const;
}

// Optional: global alias for manual refresh in DevTools (dev only)
if (typeof window !== 'undefined' && import.meta.env?.DEV === true) {
  Object.assign(window, { refreshPrices });
}

